import { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Alert,
    useTheme,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { FoodsIcon } from "@foodpilot/foods";
import {
    useGetTopics,
    useCreateTopic,
    useUpdateTopic,
    Topic,
    CreateTopicData,
} from "../../../../api/client-api/social/useTopics";
import { useTranslation } from "react-i18next";

interface TopicFormData extends CreateTopicData {
    color: string;
    active: boolean;
}

export const TopicsAdmin = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTopic, setEditingTopic] = useState<Topic | null>(null);

    const { data: topics, isLoading, isError } = useGetTopics();
    const createTopic = useCreateTopic();
    const updateTopic = useUpdateTopic();

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<TopicFormData>({
        defaultValues: {
            name: "",
            description: "",
            color: "#1976D2",
            active: true,
        },
    });

    const handleOpenDialog = (topic?: Topic) => {
        if (topic) {
            setEditingTopic(topic);
            reset({
                name: topic.name,
                description: topic.description,
                color: topic.color,
                active: topic.active,
            });
        } else {
            setEditingTopic(null);
            reset({
                name: "",
                description: "",
                color: "#1976D2",
                active: true,
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingTopic(null);
        reset();
    };

    const onSubmit = async (data: TopicFormData) => {
        try {
            if (editingTopic) {
                await updateTopic.mutateAsync({
                    id: editingTopic.id,
                    data: {
                        name: data.name,
                        description: data.description,
                        color: data.color,
                        active: data.active,
                    },
                });
            } else {
                await createTopic.mutateAsync({
                    name: data.name,
                    description: data.description,
                    color: data.color,
                    active: data.active,
                });
            }
            handleCloseDialog();
        } catch (error) {
            // Error handled by mutation
        }
    };

    if (isLoading) {
        return (
            <Card>
                <CardContent>
                    <Typography>{t("social.topics.loading", "Loading topics...")}</Typography>
                </CardContent>
            </Card>
        );
    }

    if (isError) {
        return (
            <Card>
                <CardContent>
                    <Alert severity="error">{t("social.topics.error.load", "Failed to load topics")}</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <Card>
                <CardHeader
                    title={t("social.topics.admin.title", "Topics Administration")}
                    avatar={<FoodsIcon icon="settings" size={2} />}
                    action={
                        <Button
                            variant="contained"
                            onClick={() => handleOpenDialog()}
                            startIcon={<FoodsIcon icon="plus" size={2} />}
                            sx={(theme) => ({
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: "white !important",
                                textTransform: "none",
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                lineHeight: "1.5em",
                                boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                    boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                },
                            })}
                        >
                            {t("social.topics.admin.create_topic", "Create Topic")}
                        </Button>
                    }
                />
                <CardContent>
                    {!topics?.length ?
                        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                            {t(
                                "social.topics.admin.no_topics",
                                "No topics created yet. Create your first topic to get started.",
                            )}
                        </Typography>
                    :   <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("social.topics.admin.name", "Name")}</TableCell>
                                        <TableCell>{t("social.topics.admin.description", "Description")}</TableCell>
                                        <TableCell>{t("social.topics.admin.color", "Color")}</TableCell>
                                        <TableCell>{t("social.topics.admin.posts", "Posts")}</TableCell>
                                        <TableCell>{t("social.topics.admin.followers", "Followers")}</TableCell>
                                        <TableCell>{t("social.topics.admin.status", "Status")}</TableCell>
                                        <TableCell>{t("social.topics.admin.actions", "Actions")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topics.map((topic) => (
                                        <TableRow key={topic.id}>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: "50%",
                                                            backgroundColor: topic.color,
                                                        }}
                                                    />
                                                    <Typography variant="body2" fontWeight={500}>
                                                        #{topic.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {topic.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={topic.color}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: topic.color + "20",
                                                        color: topic.color,
                                                        fontFamily: "monospace",
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{topic.postCount}</TableCell>
                                            <TableCell>{topic.followerCount}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        topic.active ?
                                                            t("social.topics.admin.active", "Active")
                                                        :   t("social.topics.admin.inactive", "Inactive")
                                                    }
                                                    size="small"
                                                    color={topic.active ? "success" : "default"}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenDialog(topic)}
                                                    title={t("social.topics.admin.edit", "Edit topic")}
                                                >
                                                    <FoodsIcon icon="edit" size={2} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                </CardContent>
            </Card>

            {/* Create/Edit Topic Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        {editingTopic ?
                            t("social.topics.admin.edit", "Edit Topic")
                        :   t("social.topics.admin.create_new_topic", "Create New Topic")}
                    </DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={3} pt={1}>
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: t("social.topics.admin.name_required", "Topic name is required"),
                                    pattern: {
                                        value: /^[a-zA-Z0-9_-]+$/,
                                        message: t(
                                            "social.topics.admin.name_pattern",
                                            "Only alphanumeric characters, underscores and hyphens allowed",
                                        ),
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t("social.topics.admin.name", "Topic Name")}
                                        fullWidth
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                        placeholder={t(
                                            "social.topics.admin.name_placeholder",
                                            "e.g., general, announcements, feedback",
                                        )}
                                    />
                                )}
                            />

                            <Controller
                                name="description"
                                control={control}
                                rules={{
                                    required: t("social.topics.admin.description_required", "Description is required"),
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t("social.topics.admin.description", "Description")}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        error={!!errors.description}
                                        helperText={errors.description?.message}
                                        placeholder={t(
                                            "social.topics.admin.description_placeholder",
                                            "Describe what this topic is about...",
                                        )}
                                    />
                                )}
                            />

                            <Controller
                                name="color"
                                control={control}
                                render={({ field }) => (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            {t("social.topics.admin.color", "Topic Color")}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <TextField {...field} type="color" sx={{ width: 80 }} />
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: "50%",
                                                    backgroundColor: field.value,
                                                    border: `2px solid ${theme.palette.divider}`,
                                                }}
                                            />
                                            <Typography variant="body2" fontFamily="monospace">
                                                {field.value}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            />

                            <Controller
                                name="active"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={field.onChange}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                                        color: (theme) => theme.palette.primary[600],
                                                    },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                        backgroundColor: (theme) => theme.palette.primary[600],
                                                    },
                                                }}
                                            />
                                        }
                                        label={t("social.topics.admin.active", "Active Topic")}
                                    />
                                )}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCloseDialog}
                            sx={{
                                border: (theme) => `1px solid ${theme.palette.primary[600]}`,
                                color: (theme) => theme.palette.primary[600],
                                textTransform: "none",
                                borderRadius: "8px",
                                padding: "8px 20px",
                                "&:hover": {
                                    border: (theme) => `1px solid ${theme.palette.primary[700]}`,
                                    color: (theme) => theme.palette.primary[700],
                                    backgroundColor: "transparent",
                                },
                            }}
                        >
                            {t("social.topics.admin.cancel", "Cancel")}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={createTopic.isLoading || updateTopic.isLoading}
                            sx={(theme) => ({
                                background: `linear-gradient(135deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 50%, ${theme.palette.primary[700]} 100%)`,
                                color: "white",
                                textTransform: "none",
                                borderRadius: "8px",
                                padding: "8px 24px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                boxShadow: "none",
                                "&:hover": {
                                    background: `linear-gradient(135deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 50%, ${theme.palette.primary[700]} 100%)`,
                                    boxShadow: "none",
                                },
                                "&:disabled": {
                                    background: "#E0E0E0",
                                    color: "#A0A0A0",
                                },
                            })}
                        >
                            {editingTopic ?
                                t("social.topics.admin.update", "Update")
                            :   t("social.topics.admin.create", "Create")}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};
